import { Component } from '@verndale/core';
import { KEYS } from '../constants';
import _debounce from 'lodash.debounce';
import { isTabletLandscape } from '../helpers';
import { focusableElements } from '../helpers';
import { clearBodyLocks, lock } from 'tua-body-scroll-lock';
import { createFocusTrap } from 'focus-trap';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      $burger: this.el._$('.header__burger'),
      $primaryNav: this.el._$('.primary-nav'),
      $primaryNavButtons: this.el._$$('.primary-nav__btn'),
      $utilityNav: this.el._$('.utility-nav'),
      $utilityNavButtons: this.el._$$('.utility-nav__link'),
      $primaryNavList: this.el._$('.primary-nav__list'),
      $logo: this.el._$('.header__logo'),
      $searchToggle: this.el._$('.header__search-toggle'),
      $searchForm: this.el._$('.header__search'),
      $submenuLinks: this.el._$$('.primary-nav__submenu-link'),
      $primaryNavItems: this.el._$$('.primary-nav__menu-item'),
      $banners: document._$$('.alert-banner')
    };

    this.primaryNavOpenClass = 'primary-nav--open';
    this.primaryNavChildActiveClass = 'primary-nav__list--child-active';
    this.burgerOpenClass = 'header__burger--open';
    this.activeMenuItemClass = 'primary-nav__menu-item--active';
    this.utilityNavExpandedClass = 'utility-nav--expanded';
    this.submenuExpandedClass = 'primary-nav__link-group--expanded';
    this.searchFormOpenClass = 'header__search--active';

    this.setTabOrder();
    this.setRoles();
    this.lastResizeWidth = window.innerWidth;
    this.focusTrap = createFocusTrap(this.el, {
      allowOutsideClick: false,
      clickOutsideDeactivates: false,
      escapeDeactivates: true,
      onDeactivate: () => {
        this.el.style.removeProperty('position');
        clearBodyLocks();
      },
      onActivate: () => {
        if (window.innerWidth < 768) {
          this.el.style.setProperty('position', 'fixed');
          const element = [
            this.dom.$primaryNavList,
            ...this.dom.$primaryNavItems
          ];
          lock(element, {
            overflowType: 'clip'
          });
        }
      }
    });

    this.setHeaderHeight();
    this.isScrolling = false;
    this.isTransitioning = false;
    this.lastScrollY = window.scrollY;
    this.hiddenBanners = [];
    const primaryNavWidth = this.dom.$primaryNav.offsetWidth;
    this.dom.$primaryNav.style.setProperty(
      '--primary-nav-width',
      `${primaryNavWidth}px`
    );
  }

  addListeners() {
    this.dom.$burger._on('click', this.togglePrimaryNavExpand.bind(this));
    this.dom.$searchToggle._on('click', this.toggleSearchExpand.bind(this));
    this.el._on('keydown', this.handleKeyDown.bind(this));
    this.el._on('click', this.handleBackBtnClick.bind(this));
    this.el._on('click', this.handlePrimaryBtnClick.bind(this));
    this.el._on('click', this.handleSubMenuClick.bind(this));
    this.el._on('click', this.handleMainLinkClick.bind(this));
    this.el._on('keydown', this.handleMainLinkClick.bind(this));
    document._on('mousedown', this.handleClickOutsidePrimary.bind(this));
    window._on('resize', _debounce(this.handleResize.bind(this), 200));
    this.dom.$searchForm._on('submit', e => e.preventDefault());
    window._on(
      'scroll',
      _debounce(this.handleScroll.bind(this), 10, { leading: true })
    );
  }

  setHeaderHeight() {
    requestAnimationFrame(() => {
      document.body.style.setProperty(
        '--header-height',
        `${this.el.offsetHeight}px`
      );
    });
  }

  isSticky() {
    if (this.isTransitioning) return;

    const header = this.el;
    const banners = document._$$('.alert-banner-enhanced');
    const stickyThreshold = 0;

    if (banners.length > 0) {
      // Check each banner's visibility
      banners.forEach(banner => {
        const bannerTop = banner.getBoundingClientRect().top;
        if (bannerTop === 0) {
          // Add to array if not already present
          if (!this.hiddenBanners.includes(banner)) {
            this.hiddenBanners.push(banner);
          }
        } else {
          // Remove from array
          const index = this.hiddenBanners.indexOf(banner);
          if (index > -1) {
            this.hiddenBanners.splice(index, 1);
          }
        }
      });

      const totalBannerHeight = this.hiddenBanners.reduce(
        (sum, banner) => sum + banner.offsetHeight,
        0
      );
      const maxBannerHeight = Math.max(
        this.hiddenBanners.map(banner => banner.offsetHeight)
      );

      const headerHeight = header.offsetHeight;
      // Only become sticky if all banners are hidden

      const shouldBeSticky =
        this.hiddenBanners.length === banners.length &&
        window.scrollY > totalBannerHeight + (headerHeight - maxBannerHeight);
      const isSticky = header.classList.contains('header--sticky');

      if (shouldBeSticky !== isSticky) {
        this.isTransitioning = true;

        if (shouldBeSticky) {
          header.classList.add('header--sticky');
        } else {
          header.classList.remove('header--sticky');
        }

        setTimeout(() => {
          this.setHeaderHeight();
          this.isTransitioning = false;
        }, 300);
      }
    } else {
      // No banners case - use simple scroll threshold
      const shouldBeSticky = window.scrollY > 1;
      const isSticky = header.classList.contains('header--sticky');

      if (shouldBeSticky !== isSticky) {
        this.isTransitioning = true;

        if (shouldBeSticky) {
          header.classList.add('header--sticky');
        } else {
          header.classList.remove('header--sticky');
        }

        setTimeout(() => {
          this.setHeaderHeight();
          this.isTransitioning = false;
        }, 300);
      }
    }
  }

  togglePrimaryNavExpand() {
    this.removeActiveMenuItems();

    const primaryNavExpanded = this.dom.$primaryNav.classList.contains(
      this.primaryNavOpenClass
    );
    this.primaryNavExpanded = !primaryNavExpanded;

    if (primaryNavExpanded || this.activeMenuItem) this.closeActiveMenuItem();

    this.dom.$burger.classList.toggle(this.burgerOpenClass);
    this.dom.$primaryNav.classList.toggle(this.primaryNavOpenClass);
    this.dom.$utilityNav.classList.toggle(this.utilityNavExpandedClass);

    this.el.style.setProperty(
      '--utility-nav-height',
      `${this.dom.$utilityNav.offsetHeight}px`
    );
    this.setUtilityNavPosition(this.dom.$primaryNavList.offsetHeight + 50);

    if (this.primaryNavExpanded) {
      this.focusTrap.activate();
    } else {
      this.focusTrap.deactivate();
    }
  }

  toggleSearchExpand() {
    const searchInput = this.el.querySelector(
      '.coveo-search-results__search-box-input'
    );

    this.dom.$searchForm.classList.toggle(this.searchFormOpenClass);
    if (
      this.dom.$searchForm.classList.contains(
        this.searchFormOpenClass && !isTabletLandscape
      )
    ) {
      this.el._$('.header__search input').focus();
    }

    if (searchInput?.value?.length > 0) {
      window.dispatchEvent(new Event('submitSearchCoveo'));
    }
  }

  setUtilityNavPosition(top) {
    this.dom.$utilityNav.style.insetBlockStart = `${top}px`;
  }

  setTabOrder() {
    let idx = 0;

    if (this.dom.$banners.length) {
      this.dom.$banners.forEach($banner => {
        idx += $banner._$$(focusableElements).length;
      });
    }

    if (isTabletLandscape()) {
      if (this.lastLayout === 'mobile') return;
      this.lastLayout = 'mobile';

      this.dom.$searchToggle?.setAttribute('tabindex', '1');
      this.dom.$logo?.setAttribute('tabindex', '2');
      this.dom.$burger?.setAttribute('tabindex', '3');

      idx = 4;

      this.dom.$primaryNavButtons.forEach(primaryNavBtn => {
        primaryNavBtn?.setAttribute('tabindex', idx);
        idx++;
      });

      this.dom.$utilityNavButtons.forEach(utilityNavBtn => {
        utilityNavBtn?.setAttribute('tabindex', idx);
        idx++;
      });

      this.dom.$submenuLinks?.forEach(submenuLink => {
        submenuLink?.removeAttribute('role');
      });
    } else {
      if (this.lastLayout === 'notMobile') return;
      this.lastLayout = 'notMobile';

      this.dom.$logo?.removeAttribute('tabindex');
      this.dom.$burger?.setAttribute('tabindex', '-1');

      idx = 2;

      this.dom.$utilityNavButtons?.forEach(utilityNavBtn => {
        utilityNavBtn?.removeAttribute('tabindex');
        idx++;
      });

      this.dom.$searchToggle?.removeAttribute('tabindex');
      idx++;

      this.dom.$primaryNavButtons?.forEach(primaryNavBtn => {
        primaryNavBtn?.removeAttribute('tabindex');
        idx++;
      });

      this.dom.$submenuLinks?.forEach(submenuLink => {
        if (!submenuLink?.closest('.primary-nav__main-link'))
          submenuLink?.setAttribute('role', 'presentation');
      });
    }
  }

  setRoles() {
    if (isTabletLandscape()) {
      this.dom.$submenuLinks.forEach($el => $el?.removeAttribute('role'));
    } else {
      this.dom.$submenuLinks.forEach($el => {
        $el?.setAttribute('role', 'none');
      });
    }
  }

  closeActiveMenuItem() {
    this.activeMenuItem?.classList.remove(this.activeMenuItemClass);
    this.activeMenuItem = null;
    this.dom.$primaryNavList.classList.remove(this.primaryNavChildActiveClass);
  }

  closeActiveSubmenuItem() {
    this.expandedSubmenu?.classList.remove(this.submenuExpandedClass);
    this.expandedSubmenu = null;
  }

  removeActiveMenuItems() {
    this.dom.$primaryNavButtons.forEach($btn => {
      $btn.classList.remove('primary-nav__btn--active');
    });
  }

  handleClickOutsidePrimary(e) {
    if (
      e.target.closest('.primary-nav') &&
      !e.target.classList.contains('primary-nav') &&
      !e.target.classList.contains('primary-nav__list')
    )
      return;
    if (e.target.closest('.utility-nav')) return;
    if (e.target.closest('.header__burger')) return;
    if (e.target.classList.contains('header__burger')) return;
    if (e.target.classList.contains('primary-nav__btn')) return;
    if (this.primaryNavExpanded || this.activeMenuItem)
      this.togglePrimaryNavExpand();

    this.removeActiveMenuItems();
  }

  handleKeyDown(e) {
    if (
      (this.primaryNavExpanded || this.activeMenuItem) &&
      e.keyCode === KEYS.ESC
    ) {
      document.activeElement.blur();
      this.togglePrimaryNavExpand();

      this.removeActiveMenuItems();
    }
  }

  handleBackBtnClick(e) {
    if (!e.target.classList.contains('primary-nav__back')) return;
    this.closeActiveMenuItem();
    this.removeActiveMenuItems();
    this.el.style.setProperty(
      '--utility-nav-height',
      `${this.dom.$utilityNav.offsetHeight}px`
    );
    this.setUtilityNavPosition(this.dom.$primaryNavList.offsetHeight + 50);
    this.dom.$primaryNavList.classList.remove(this.primaryNavChildActiveClass);
  }

  handlePrimaryBtnClick(e) {
    if (!e.target.classList.contains('primary-nav__btn')) return;

    this.dom.$primaryNavButtons.forEach($btn => {
      $btn.classList.remove('primary-nav__btn--active');
    });

    const menuItem = this.el._$(`#${e.target.dataset.id}`);

    if (menuItem !== this.activeMenuItem) {
      this.closeActiveSubmenuItem();
      menuItem.classList.add(this.activeMenuItemClass);
      this.closeActiveMenuItem();
      this.activeMenuItem = menuItem;
      this.el.style.setProperty(
        '--utility-nav-height',
        `${this.dom.$utilityNav.offsetHeight}px`
      );
      this.setUtilityNavPosition(this.activeMenuItem.offsetHeight + 50);
      this.dom.$primaryNavList.classList.add(this.primaryNavChildActiveClass);
      e.target.classList.add('primary-nav__btn--active');
    } else {
      this.togglePrimaryNavExpand();
    }
  }

  handleSubMenuClick(e) {
    if (e.target.classList.contains('primary-nav__sublink')) {
      this.togglePrimaryNavExpand();
    }
    if (!e.target.classList.contains('primary-nav__submenu-link')) return;

    const submenuContainer = e.target.parentNode;

    if (this.expandedSubmenu && this.expandedSubmenu !== submenuContainer) {
      this.expandedSubmenu.classList.remove(this.submenuExpandedClass);
    }

    submenuContainer.classList.toggle(this.submenuExpandedClass);
    this.expandedSubmenu = submenuContainer;

    this.el.style.setProperty(
      '--utility-nav-height',
      `${this.dom.$utilityNav.offsetHeight}px`
    );
    this.setUtilityNavPosition(this.activeMenuItem.offsetHeight + 50);
  }

  handleMainLinkClick(e) {
    let href, target;

    if (isTabletLandscape()) {
      href = e.target.parentNode.parentNode.dataset.href;
      target = e.target.parentNode.parentNode.dataset.target;
    } else {
      if (!e.target.classList.contains('primary-nav__main-link')) return;
      href = e.target.dataset.href;
      target = e.target.dataset.target;
    }

    if (e.keyCode) {
      if (e.keyCode !== KEYS.ENTER) {
        return;
      }
    }

    if (href) {
      target ? window.open(href, target) : (window.location.href = href);
    }
  }

  handleResize() {
    this.setHeaderHeight();
    const primaryNavExpanded = this.dom.$primaryNav.classList.contains(
      this.primaryNavOpenClass
    );
    const isResizeHorizontal = this.lastResizeWidth !== window.innerWidth;
    this.lastResizeWidth = window.innerWidth;

    if (primaryNavExpanded && isResizeHorizontal) {
      this.togglePrimaryNavExpand();
    }
    this.setTabOrder();

    const primaryNavWidth = this.dom.$primaryNav.offsetWidth;
    this.dom.$primaryNav.style.setProperty(
      '--primary-nav-width',
      `${primaryNavWidth}px`
    );
  }

  handleScroll() {
    this.isSticky();
  }
}

export default Module;
